import { css, html } from 'lit';
import './d-select-organization.js';
import './d-organization-subscription.js';
import './d-organization-profile.js';
import './d-organization-users.js';
import '../../library/fields/d-closer.js';
import { customElement, property } from 'lit/decorators.js';
import type { EmployeeViewModelWithName } from 'src/store/types.js';
import '../../library/fields/d-expansion.js';
import { ResponsiveContainer } from 'src/library/elements/responsive-container.js';
import { headerViewStyles } from './header-view-styles.js';

import type { UiSettings } from '../../store/selectors/uiSettings';
import type { FeatureStates } from '../../store/selectors/featureStates';
import type { SettingsMenuItem } from './settings-menu-item';
import type { UsersSettingsItem } from './users-settings-item';
import type { SubscriptionSettingsItem } from './subscription-settings-item';
import type { SettingsItem } from './settings-item';

/**
 * Display settings for current user and organization. The available settings are dependent on the access level of
 * the current user.
 *
 * @fires open-user-settings
 * @fires close-settings
 *
 */
@customElement('d-organization-settings')
export class DOrganizationSettings extends ResponsiveContainer {
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: flex;
        justify-content: flex-end;
      }

      .meta-content-wrapper {
        display: flex;
        flex-direction: column;
        max-height: calc((var(--vh, 1vh) * 100) - var(--applicationHeaderHeight));
      }

      :host(.width600) .meta-content-wrapper {
        flex-direction: row;
        margin: 12px 20px 20px 32px;
        max-height: calc((var(--vh, 1vh) * 100) - var(--applicationHeaderHeight) - 32px);
      }

      d-closer {
        position: absolute;
        top: 10px;
        right: 16px;
        z-index: 100;
      }

      .page {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        font-family: var(--mainSans);
        font-weight: normal;
        font-size: 15px;
        text-transform: none;
        letter-spacing: normal;
        line-height: 160%;
        color: black;
        overflow-y: auto;
      }
    `,
  ];
  @property({ type: Boolean, reflect: true })
  open = false;
  @property({ type: Object })
  user: any = {};
  @property({ type: Object })
  organization: any = {};
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Array })
  employees: EmployeeViewModelWithName[] = [];
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: String })
  selectedPage = '';
  @property({ type: Array })
  settingsMenuItems: SettingsMenuItem[] = [];
  @property({ type: Object })
  settingsItem!: SettingsItem;
  @property({ type: Boolean })
  storybook = false;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Object })
  uiSettings: UiSettings = {
    isSimplifiedUi: false,
  };

  get selectMenuItem() {
    return this.settingsMenuItems.find((item) => item.id === this.settingsItem.type);
  }

  _getEmployee(employees, username) {
    const compareStrings = function (string1, string2, ignoreCase, useLocale) {
      if (string1 && string2) {
        if (ignoreCase) {
          if (useLocale) {
            string1 = string1.toLocaleLowerCase();
            string2 = string2.toLocaleLowerCase();
          } else {
            string1 = string1.toLowerCase();
            string2 = string2.toLowerCase();
          }
        }
        return string1 === string2;
      }
      return false;
    };

    const areEqual = function (e1, e2) {
      return compareStrings(e1, e2, true, true);
    };

    const list = employees ?? [];

    const employees1 = list.filter(function (e) {
      return areEqual(e.email, username);
    });

    if (employees1.length > 0) {
      return employees1[0];
    }
  }

  renderSettingsItem() {
    switch (this.settingsItem.type) {
      case 'organizations':
        return html`<d-select-organization
          .organizations=${this.settingsItem.organizations}
          .organizationId=${this.organizationId}
          .user=${this.user}
        >
        </d-select-organization>`;
      case 'profile':
        return html`<d-organization-profile
          employee="${this._getEmployee(this.employees, this.user.username)}"
          @close-settings=${() => this.closeSettings()}
          organization="${this.organization}"
          .organizationId=${this.organizationId}
          .user=${this.settingsItem.user}
          .newEmail=${this.settingsItem.user.username}
          .isEmployee=${this.settingsItem.isEmployee}
          .alerts=${this.settingsItem.alerts}
          .receiveReminders=${this.settingsItem.reminders}
          .features=${this.settingsItem.features}
          .ownerEmail=${this.settingsItem.ownerEmail}
          .featureStates=${this.featureStates}
          .storybook=${this.storybook}
          .uiSettings=${this.uiSettings}
        >
        </d-organization-profile>`;
      case 'subscription':
        return this.renderSettings(this.settingsItem);
      case 'users':
        return this.renderUsers(this.settingsItem);
    }
  }

  render() {
    return html`
      <d-expansion ?opened=${this.open}>
        <div class="meta-content-wrapper">
          <d-closer color="white" @click=${this.closeSettings}></d-closer>
          <div class="page">
            <div class="header">${this.selectMenuItem?.name}</div>
            <div class="content">${this.renderSettingsItem()}</div>
          </div>
        </div>
      </d-expansion>
    `;
  }

  private renderSettings(settingsItem: SubscriptionSettingsItem) {
    return html`<d-organization-subscription
      @update-subscription=${() => this.closeSettings()}
      .subscription=${settingsItem.subscription}
      .availableOwnerEmployees=${settingsItem.administratorEmployeesList.map((e) => {
        return { value: e.email, text: e.name };
      })}
      .singleUserVersionAvailable=${settingsItem.singleUserVersionAvailable}
      .elmaLookup=${async (q) => await settingsItem.elmaLookup(q)}
    >
    </d-organization-subscription>`;
  }

  private renderUsers(settingsItem1: UsersSettingsItem) {
    const currentUser = settingsItem1.users.find((u) => u.email === settingsItem1.currentUserEmail);
    if (currentUser === undefined) {
      throw new Error('Illegal state (E409), no current user');
    }
    return html`<d-organization-users
      .users=${settingsItem1.users}
      .accountOwnerEmail=${settingsItem1.ownerEmail}
      .currentUser=${currentUser}
      .featureStates=${this.featureStates}
      .organizationId=${this.organizationId}
    >
    </d-organization-users>`;
  }

  private closeSettings() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('close-settings', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-settings': DOrganizationSettings;
  }
}
